// lib/api.js

import axios from 'axios';
const baseURL = process.env.NEXT_PUBLIC_BASE_URL;
// import { useEffect } from "react";
// const baseURL = process.env.NEXT_PUBLIC_baseURL_URL;

// attach token if present
export function getAuthHeaders() {
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    return token ? { Authorization: `Bearer ${token}` } : {};
}

// Cache configuration
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds
const CACHE_PREFIX = 'api_cache_';

// Helper function to get cached data
const getCachedData = (cacheKey) => {
    if (typeof window === 'undefined') return null;

    try {
        const cached = localStorage.getItem(cacheKey);
        if (!cached) return null;

        const { data, timestamp } = JSON.parse(cached);
        const now = Date.now();

        // Check if cache is still valid
        if (now - timestamp < CACHE_TTL) {
            return data;
        }

        // Cache expired, remove it
        localStorage.removeItem(cacheKey);
        return null;
    } catch (error) {
        console.error('Error reading cache:', error);
        return null;
    }
};

// Helper function to set cached data
const setCachedData = (cacheKey, data) => {
    if (typeof window === 'undefined') return;

    try {
        const cacheData = {
            data,
            timestamp: Date.now()
        };
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
        console.error('Error setting cache:', error);
        // If storage is full, try to clear old cache entries
        try {
            const keys = Object.keys(localStorage);
            const cacheKeys = keys.filter(key => key.startsWith(CACHE_PREFIX));
            // Remove oldest 10 cache entries
            cacheKeys.slice(0, 10).forEach(key => localStorage.removeItem(key));
            // Retry setting cache
            localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        } catch (retryError) {
            console.error('Error retrying cache set:', retryError);
        }
    }
};

// Helper function to generate cache key for getData
const getDataCacheKey = (type, params, customAPI) => {
    const apiPath = customAPI ? customAPI : 'wp/v2';
    return `${CACHE_PREFIX}getdata_${apiPath}_${type}_${params || ''}`;
};

// get posts / Articles
// export const getPosts = ({ data: posts }) => axios.get(`${baseURL}/api/wp/v2/posts?_embed`);
// export const getPosts = () => axios.get(`${baseURL}/api/wp/v2/posts?_embed`).then(res => res.data);
export const getData = (type, params, customAPI) => {
    const fullUrl = `${baseURL}api/${customAPI ? customAPI : 'wp/v2/'}${type}?${params}`;
    const cacheKey = getDataCacheKey(type, params, customAPI);

    // Check cache first
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
        return Promise.resolve(cachedData);
    }

    // If not in cache, fetch from API
    return axios.get(fullUrl).then(res => {
        const response = res.data;

        // Cache the response
        setCachedData(cacheKey, response);

        return response;
    }).catch(error => {
        console.error('getData error:', error);
        console.error('Error details:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
            url: fullUrl
        });
        throw error;
    });
}

// Helper function to generate cache key for getDataWithPagination
const getCacheKey = (type, page, perPage, params) => {
    return `${CACHE_PREFIX}${type}_${page}_${perPage}_${params || '_embed'}`;
};

// Clear cache for a specific post type or all cache
export const clearApiCache = (type = null) => {
    if (typeof window === 'undefined') return;

    try {
        const keys = Object.keys(localStorage);
        const cacheKeys = keys.filter(key => key.startsWith(CACHE_PREFIX));

        if (type) {
            // Clear cache for specific type
            const typePrefix = `${CACHE_PREFIX}${type}_`;
            cacheKeys
                .filter(key => key.startsWith(typePrefix))
                .forEach(key => localStorage.removeItem(key));
        } else {
            // Clear all API cache
            cacheKeys.forEach(key => localStorage.removeItem(key));
        }
    } catch (error) {
        console.error('Error clearing cache:', error);
    }
};

/** Read REST pagination headers (axios may normalize keys; proxies may vary casing). */
function getWpRestHeader(headers, name) {
    if (!headers) return undefined;
    const lower = String(name).toLowerCase();
    const direct = headers[lower] ?? headers[name];
    if (direct != null && direct !== '') return direct;
    for (const key of Object.keys(headers)) {
        if (String(key).toLowerCase() === lower) return headers[key];
    }
    return undefined;
}

// get posts with pagination support
export const getDataWithPagination = (type, page = 1, perPage = 10, params = '_embed') => {
    const queryParams = `${params}&per_page=${perPage}&page=${page}`;
    const fullUrl = `${baseURL}api/wp/v2/${type}?${queryParams}`;
    const cacheKey = getCacheKey(type, page, perPage, params);

    // Check cache first
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
        return Promise.resolve(cachedData);
    }

    // If not in cache, fetch from API
    return axios.get(fullUrl).then(res => {
        const totalRaw = getWpRestHeader(res.headers, 'x-wp-total');
        const totalPagesRaw = getWpRestHeader(res.headers, 'x-wp-totalpages');
        let total = parseInt(totalRaw ?? '0', 10) || 0;
        let totalPages = parseInt(totalPagesRaw ?? '0', 10) || 0;
        if (total > 0 && perPage > 0) {
            totalPages = Math.max(1, Math.ceil(total / perPage));
        } else if (totalPages < 1 && total < 1) {
            totalPages = 1;
        }

        const response = {
            data: res.data,
            total,
            totalPages,
            currentPage: page
        };

        // Cache the response
        setCachedData(cacheKey, response);

        return response;
    }).catch(error => {
        console.error('getDataWithPagination error:', error);
        console.error('Error details:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
            url: fullUrl
        });
        throw error;
    });
};

//  get feature image 
export const getFeaturedImage = (postId) =>
    axios.get(`${baseURL}/posts/${postId}?_embed`, { headers: getAuthHeaders() }).then(res => res.data);



// fetch current user
export const fetchUser = () =>
    // to return Email 
    // Ref : https://chatgpt.com/share/687f5feb-8df0-800a-8a03-adfed1101b54
    axios.get(`${baseURL}api/wp/v2/users/me?context=edit`, { headers: getAuthHeaders() })
        .then(res => res.data);



// update profile fields
// export const updateUser = ({ name, email, password, meta, avatar_id }) =>
export const updateUser = ({ name, description, email, password, avatar_id, user_job, cert_name, user_company, country, user_linkedin_profile, behance, user_extra_url, user_mobile, private_profile }) =>
    axios.post(
        `${baseURL}api/wp/v2/users/me`,
        { ...(name && { name }), ...(description && { description }), ...(email && { email }), ...(password && { password }), ...(avatar_id && { avatar_id }), ...(user_job && { user_job }), ...(cert_name && { cert_name }), ...(user_company && { user_company }), ...(country && { country }), ...(user_linkedin_profile && { user_linkedin_profile }), ...(behance && { behance }), ...(user_extra_url && { user_extra_url }), ...(user_mobile && { user_mobile }), private_profile: private_profile !== undefined ? private_profile : false },
        // { ...(name && { name }), ...(email && { email }), ...(password && { password }), ...(meta && { meta }), ...(avatar_id && { avatar_id }) },
        { headers: { 'Content-Type': 'application/json', ...getAuthHeaders() } }
    ).then(res => res.data);




// upload media (avatar)
export const uploadAvatar = (file, fileName) => {
    // if (!file || file.size / 1000 > 1000) {
    //     alert('حجم الصورة يجب أن يكون أقل من 1 ميجابايت');
    // } else {
    const form = new FormData();
    form.append('file', file);
    form.append('title', fileName);
    return axios.post(`${baseURL}api/wp/v2/media`, form, {
        headers: { 'Content-Type': 'multipart/form-data', ...getAuthHeaders() }
    }).then(res => res.data);
    // }
};

// delete user account
export const deleteUserAccount = () =>
    axios.delete(
        `${baseURL}api/wp/v2/users/me`,
        {
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            data: { force: true, reassign: 0 } // Force delete and don't reassign content
        }
    ).then(res => res.data);



export const validateToken = (token) => {
    return axios.post(
        `${baseURL}api/jwt-auth/v1/token/validate`,
        {},
        {
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
        }
    ).then(res => {
        console.log(res.data + token);
        return res.data.data.status == 200 ? true : false;
    });
}


export const isLoggedIn = async () => {
    if (typeof window === 'undefined') return false;
    const token = window.localStorage.getItem('token');
    if (!token) return false;
    // 🔍 Decode JWT to check expiry locally (optional)
    try {

        let valid = await validateToken(token);
        // console.log(valid);

        const payload = JSON.parse(atob(token.split('.')[1])); // base64 decode JWT
        const now = Date.now() / 1000;
        if (!valid && payload.exp && payload.exp < now) {
            console.warn('Token expired');
            return false;
        } else {
            return true;
        }
    } catch {
        console.warn('Invalid token format');
        return false;
    }
};


// fetch users with specific role
export const fetchPblicUsers = (role) => {
    const url = role
        ? `${baseURL}api/users/public-users?role=${role}&per_page=100`
        : `${baseURL}api/users/public-users?per_page=100`;

    return axios.get(url, { headers: getAuthHeaders() })
        .then(res => res.data);
};

// fetch author information and post count
export const fetchAuthorInfo = (authorId) =>
    axios.get(`${baseURL}api/wp/v2/users/${authorId}?_embed`, { headers: getAuthHeaders() })
        .then(res => res.data);

// fetch posts count by author
export const fetchAuthorPostsCount = (authorId, postType = 'posts') =>
    axios.get(`${baseURL}api/wp/v2/${postType}?author=${authorId}&per_page=1`, { headers: getAuthHeaders() })
        .then(res => {
            const totalPosts = res.headers['x-wp-total'] || 0;
            return parseInt(totalPosts);
        });

// fetch new jobs published today
export const fetchNewJobsToday = async () => {
    try {
        const today = new Date().toISOString().split('T')[0];
        const response = await axios.get(`${baseURL}api/wp/v2/job?after=${today}T00:00:00&per_page=100`, {
            headers: getAuthHeaders()
        });

        // Count jobs published today
        const jobs = response.data;
        const todayJobs = jobs.filter(job => {
            const jobDate = new Date(job.date).toISOString().split('T')[0];
            return jobDate === today;
        });

        return { count: todayJobs.length, jobs: todayJobs };
    } catch (error) {
        console.error('Error fetching new jobs:', error);
        return { count: 0, jobs: [] };
    }
};

// fetch comments for a post
export const getComments = (postId) =>
    axios.get(`${baseURL}api/wp/v2/comments?post=${postId}&per_page=20`, { headers: getAuthHeaders() })
        .then(res => res.data);

// post a new comment
export const postComment = (postId, content) =>
    axios.post(`${baseURL}api/wp/v2/comments`, {
        post: postId,
        content: content
    }, { headers: getAuthHeaders() }).then(res => res.data);

// search content across multiple post types
export const searchContent = async (query, page = 1, perPage = 10) => {
    if (!query || query.trim() === '') {
        return {
            data: [],
            total: 0,
            totalPages: 0,
            currentPage: page
        };
    }

    const postTypes = ['posts', 'figplg', 'figtemp', 'challenge', 'job', 'podcast', 'project'];
    const searchQuery = encodeURIComponent(query.trim());

    try {
        // Search all post types in parallel
        const searchPromises = postTypes.map(type => {
            return getDataWithPagination(type, page, perPage, `search=${searchQuery}&_embed`)
                .then(response => ({
                    ...response,
                    postType: type
                }))
                .catch(error => {
                    console.error(`Error searching ${type}:`, error);
                    return {
                        data: [],
                        total: 0,
                        totalPages: 0,
                        currentPage: page,
                        postType: type
                    };
                });
        });

        const results = await Promise.all(searchPromises);

        // Aggregate results and add post type metadata
        const aggregatedData = [];
        let totalResults = 0;

        results.forEach(result => {
            const postsWithType = result.data.map(post => ({
                ...post,
                postType: result.postType
            }));
            aggregatedData.push(...postsWithType);
            totalResults += result.total;
        });

        // Sort by date (newest first)
        aggregatedData.sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            return dateB - dateA;
        });

        // Calculate pagination for aggregated results
        const startIndex = (page - 1) * perPage;
        const endIndex = startIndex + perPage;
        const paginatedData = aggregatedData.slice(startIndex, endIndex);
        const totalPages = Math.ceil(totalResults / perPage);

        return {
            data: paginatedData,
            total: totalResults,
            totalPages: totalPages,
            currentPage: page
        };
    } catch (error) {
        console.error('Error in searchContent:', error);
        return {
            data: [],
            total: 0,
            totalPages: 0,
            currentPage: page
        };
    }
};

// subscribe to newsletter
export const subscribeToNewsletter = (email, firstName, lastName = '') =>
    axios.post(
        // `${baseURL}api/newsletter/subscribe`,
        'https://gohodhod.com/api/v1/subscribers',
        JSON.stringify({
            email: email,
            first_name: firstName,
            last_name: lastName
        }),
        { headers: { 'Content-Type': 'application/json', Authorization: `Bearer 91|jS1oPyoaPMWcwBdz79isfDbntuyI3nYR1tHLhIox` } }
    ).then(res => res);